import apiAxios from './apiAxios';

export const debounce = (func, delay) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

const getEmployeeOption = async (url, inputValue, justShowName = false, hasPage = true) => {
  try {
    const newUrl = hasPage ? `${url}?page=1&per_page=30&q=${inputValue}` : url
    const response = await apiAxios.get(newUrl);
    const { data } = response;

    const options = data && data.map((item) => {
      if (item.attributes) {
        return { value: justShowName ? item.attributes.name : item.attributes.id, label: item.attributes.name };
      } else {
        return { value: justShowName ? item.name : item.id, label: item.name };
      }
    });

    return options.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const filterOptions = async (url, inputValue, justShowName = false) => {
  try {
    const response = await apiAxios.get(`${url}?page=1&per_page=30&q=${inputValue}`);
    const { data } = response;

    const options = data && data.map((item) => {

      if (item.attributes) {
        return { value: justShowName ? item.attributes.name : item.attributes.id, label: item.attributes.name };
      } else {
        return { value: justShowName ? item.name : item.id, label: item.name };
      }
    });

    return options.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()));
  } catch (error) {
    console.error(error);
    return [];
  }
};

// Permission-free employee picker endpoint (same data/params as active_list but
// never trips CanCanCan 403). All EmployeeSelectField loaders below use this.
const employeUrl = '/organization/employees/public_list';

// Rich employee options: value/label = name (the code), plus first_name /
// last_name / full_name / email so the EmployeeSelectField renderer can show
// the person's identity (avatar + name + email), not just the bare code.
// Option shape: { value, label, firstName, lastName, fullName, email }.
// `valueField` picks what lands in option.value, i.e. what the form stores:
//   'name' — the employee code, used wherever the backend resolves names itself
//            (e.g. Travel::Booking#cc_emails_optional → by_user_names(...).pluck(:email));
//   'id'   — the employee record id, used by fields that store employee_ids.
// The label stays the code either way, so both render identically.
const getEmployeeDetailedOptions = async (inputValue, valueField = 'name') => {
  try {
    const q = inputValue || '';
    const { data } = await apiAxios.get(`${employeUrl}?page=1&per_page=30&q=${encodeURIComponent(q)}`);
    if (!Array.isArray(data)) return [];

    return data
      .map((item) => {
        const a = item.attributes || item;
        return {
          value:     valueField === 'id' ? (a.id ?? item.id) : a.name,
          label:     a.name,
          firstName: a.first_name || null,
          lastName:  a.last_name  || null,
          fullName:  a.full_name  || null,
          email:     a.email      || null,
        };
      })
      .filter((o) => o.label && o.label.toLowerCase().includes(q.toLowerCase()));
  } catch (error) {
    console.error(error);
    return [];
  }
};

// Returns a NEW debounced employee loader each call — use one per AsyncSelect
// instance to prevent multiple selects sharing the same debounce timer.
export const makeEmployeeLoader = () =>
  debounce((inputValue, callback) => {
    getEmployeeDetailedOptions(inputValue).then(callback);
  }, 300);

// Same rich options, but option.value is the employee id — for fields that store
// employee_ids (travel booking travellers) rather than names.
export const makeEmployeeIdLoader = () =>
  debounce((inputValue, callback) => {
    getEmployeeDetailedOptions(inputValue, 'id').then(callback);
  }, 300);

export const debouncedEmployees = debounce((inputValue, callback, employeeType = 'all') => {
  let url;
  url = employeeType === 'all' ? `${employeUrl}?q=${inputValue}` : `${employeUrl}?employee_type=${employeeType}&q=${inputValue}`;
  const urlNew = `${url}&page=1&per_page=30`;

  getEmployeeOption(urlNew, inputValue, true, false).then((filteredOptions) => {
    callback(filteredOptions);
  });
}, 300);

const getEmployeeOptionWithDetails = async (employeeType, inputValue) => {
  try {
    const typeParam = employeeType !== 'all' ? `&employee_type=${employeeType}` : '';
    const url = `${employeUrl}?page=1&per_page=30&q=${inputValue}${typeParam}`;
    const response = await apiAxios.get(url);
    const { data } = response;
    if (!data) return [];

    return data
      .map((item) => {
        const attrs = item.attributes || item;
        return {
          value:     attrs.name,
          label:     attrs.name,
          firstName: attrs.first_name || null,
          lastName:  attrs.last_name  || null,
          email:     attrs.email      || null,
          fullName:  attrs.full_name  || null,
        };
      })
      .filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()));
  } catch (_) {
    return [];
  }
};

// Returns a fresh debounced loader per call — prevents shared timer collisions.
// Options include: { value, label, email, fullName } for rich rendering.
export const makeEmployeeLoaderWithDetails = (employeeType = 'all') =>
  debounce((inputValue, callback) => {
    getEmployeeOptionWithDetails(employeeType, inputValue).then(callback);
  }, 300);

// Rich EMAIL-recipient options for RecipientSelect (value = email so To/Cc stay
// email strings). Employees without an email are excluded (can't be a recipient).
// Option shape: { value, label, email, name, fullName, title, avatarUrl }.
const getRecipientOptions = async (employeeType, inputValue) => {
  try {
    const typeParam = employeeType && employeeType !== 'all' ? `&employee_type=${employeeType}` : '';
    const url = `${employeUrl}?page=1&per_page=30&q=${inputValue || ''}${typeParam}`;
    const { data } = await apiAxios.get(url);
    if (!data) return [];

    return data
      .map((item) => {
        const attrs = item.attributes || item;
        return {
          value:    attrs.email,
          label:    attrs.full_name || attrs.name,
          email:    attrs.email,
          name:     attrs.name,
          fullName: attrs.full_name || null,
          title:    attrs.title     || null,
          avatarUrl: attrs.avatar_url || null,
        };
      })
      .filter((opt) => !!opt.value);
  } catch (error) {
    console.error(error);
    return [];
  }
};

// Fresh debounced recipient loader per call (one per select instance).
export const makeRecipientLoader = (employeeType = 'all') =>
  debounce((inputValue, callback) => {
    getRecipientOptions(employeeType, inputValue).then(callback);
  }, 300);
